import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { countBy, keyBy } from 'lodash-es';
import { Spinner, TableList } from 'seagull';
import { useFetchAgreementStudioData } from '~/components/AgreementStudioPage/hooks/useFetchAgreementStudioData';
import GenericError from '~/components/GenericError/GenericError';
import { useIoContext, useStoreActions } from '~/contexts';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useOnMount } from '~/hooks/useOnMount';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useLocaleKeys } from '~/i18n';
import { useStoreStatic } from '~/store/store-context';
import { editDraftsTableHeaderColumnDefs, selectVisibleDrafts, sortDrafts } from './EditDraftsTable.utils';
import EditDraftsTableRow from './EditDraftsTableRow';
import { ColumnSortId } from './types';
export function EditDraftsTableWithData(props) {
    const { visibleColumns, search, selected, setSelected, aiFilteredIds } = props;
    const { getState } = useStoreStatic();
    const { getNow } = useIoContext();
    const { t: editTableTexts } = useLocaleKeys('pages.draftsDashboard.editTable');
    const columnsTexts = editTableTexts.columns;
    const [drafts, setDrafts] = useState(props.drafts);
    useEffect(()=>setDrafts(props.drafts), [
        props.drafts
    ]);
    const [sort, setSort] = useState({
        sortId: ColumnSortId.LastModified,
        sort: 'desc'
    });
    const { contacts } = useContacts();
    const headerItems = editDraftsTableHeaderColumnDefs(visibleColumns, columnsTexts);
    const contactMap = useMemo(()=>keyBy(contacts || [], (c)=>c.id()), [
        contacts
    ]);
    const getContact = useCallback((draft)=>{
        const clientContactId = draft.clientContactId();
        return clientContactId ? contactMap[clientContactId] || null : null;
    }, [
        contactMap
    ]);
    const anchorSampleLabel = editTableTexts.anchorSample();
    const unnamedDraftLabel = editTableTexts.unnamedDraft();
    const sortedAndFilteredDrafts = useMemo(()=>{
        var _getState_userInfo_businessSettings, _getState_userInfo;
        const filteredDrafts = selectVisibleDrafts({
            drafts,
            getContact,
            search,
            anchorSampleLabel,
            aiFilteredIds
        });
        const sortedDrafts = sortDrafts({
            drafts: filteredDrafts,
            sortId: sort.sortId,
            sort: sort.sort,
            introductoryMessages: (_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : (_getState_userInfo_businessSettings = _getState_userInfo.businessSettings()) === null || _getState_userInfo_businessSettings === void 0 ? void 0 : _getState_userInfo_businessSettings.introductoryMessages,
            getContact,
            anchorSampleLabel,
            unnamedDraftLabel
        });
        return sortedDrafts;
    }, [
        drafts,
        search,
        sort,
        getContact,
        anchorSampleLabel,
        unnamedDraftLabel,
        aiFilteredIds
    ]);
    function updateDraft(updatedDraft) {
        const withLastModifiedOn = updatedDraft.setLastModifiedOn(getNow().getTime());
        setDrafts((drafts)=>drafts.map((d)=>d.id() === updatedDraft.id() ? withLastModifiedOn : d));
    }
    const deleteDraft = (draft)=>{
        setDrafts((drafts)=>drafts.filter((d)=>d.id() !== draft.id()));
    };
    const onToggleAllSelection = useCallback((selectAll)=>setSelected(drafts.reduce((acc, d)=>Object.assign(acc, {
                [d.id()]: selectAll
            }), {})), [
        drafts
    ]);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": "edit-drafts-table",
        children: /*#__PURE__*/ _jsx(TableList.Provider, {
            hasActionColumn: true,
            columnDefs: headerItems,
            expandableRows: true,
            expandableRowsVariant: "clickableIcon",
            enableRowSelection: true,
            selectedRows: selected,
            onSelectionChange: setSelected,
            numRows: drafts.length,
            numSelectedRows: numSelected,
            onToggleAllSelection: onToggleAllSelection,
            sort: {
                sort: sort.sort,
                colIndex: headerItems.findIndex((item)=>item.sortId === sort.sortId)
            },
            onSortChange: (tableListSort)=>{
                if (!tableListSort) {
                    setSort({
                        sortId: ColumnSortId.PrimaryContact,
                        sort: 'asc'
                    });
                } else {
                    setSort({
                        sortId: headerItems[tableListSort.colIndex].sortId,
                        sort: tableListSort.sort
                    });
                }
            },
            children: /*#__PURE__*/ _jsx(TableList.Structure, {
                header: /*#__PURE__*/ _jsx(TableList.Header, {
                    stickyToTop: true,
                    items: headerItems,
                    testId: "edit-drafts-table-header"
                }),
                content: /*#__PURE__*/ _jsx(_Fragment, {
                    children: sortedAndFilteredDrafts.map((draft, index)=>/*#__PURE__*/ _jsx(EditDraftsTableRow, {
                            draft: draft,
                            index: index,
                            visibleColumns: visibleColumns,
                            getContact: getContact,
                            updateDraft: updateDraft,
                            deleteDraft: deleteDraft
                        }, draft.id()))
                })
            })
        })
    });
}
const EditDraftsTable = /*#__PURE__*/ forwardRef((param, ref)=>{
    let { visibleColumns, search, selected, setSelected, aiFilteredIds } = param;
    const { api } = useStoreActions();
    const fetchDrafts = ()=>api.draft.getFullDrafts({
            limit: 1000,
            page: 1
        });
    const { refresh, renderRestfulState } = useRestfulWrapper({
        fetch: fetchDrafts
    });
    const fetchAgreementStudioData = useFetchAgreementStudioData({
        disableLoader: true,
        disableAgreements: true,
        disableProposals: true
    });
    useOnMount(()=>{
        fetchAgreementStudioData();
    });
    useImperativeHandle(ref, ()=>({
            refresh
        }));
    return renderRestfulState({
        renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                centeredInBox: true,
                centerBoxHeight: "100%",
                testId: "edit-drafts-table-spinner"
            }),
        renderError: ()=>/*#__PURE__*/ _jsx(GenericError, {
                testId: "edit-drafts-table-error"
            }),
        renderChild: (param)=>{
            let { data } = param;
            return /*#__PURE__*/ _jsx(EditDraftsTableWithData, {
                drafts: data,
                visibleColumns: visibleColumns,
                search: search,
                selected: selected,
                setSelected: setSelected,
                aiFilteredIds: aiFilteredIds
            });
        }
    });
});
export default EditDraftsTable;
